import {checkFilterQuerySync, getFilterValue, isFilterBlock, sanitizeOperators} from './utils'
import type {AnyBlock, FilterBlock, FilterQuery} from './types'
import {Strings} from './constants/strings'

export class FilterQueryRequest implements FilterQuery {
  #raw: string
  #blocks: AnyBlock[]
  #errors: Set<string> | undefined
  activeBlockId: number
  isValidated: boolean = false
  #valid: boolean
  cachedFilterCount: number = 0

  constructor(raw: string, blocks: AnyBlock[], activeBlockId = -1, isValidated = false) {
    this.update(raw, blocks, activeBlockId, isValidated)
  }

  get blocks() {
    return this.#blocks
  }

  get isValid() {
    return this.#valid
  }

  get raw() {
    return this.#raw
  }

  get errors() {
    return this.#errors ? Array.from(this.#errors) : []
  }

  update(raw: string, blocks: AnyBlock[], activeBlockId?: number, isValidated?: boolean) {
    this.#blocks = blocks
    this.#raw = raw
    if (activeBlockId !== undefined) {
      this.activeBlockId = activeBlockId
    }
    if (isValidated !== undefined) {
      this.isValidated = isValidated
    }
    if ((raw.match(/"/g) ?? []).length % 2 !== 0) {
      this.#valid = false
    } else {
      this.#valid = true
      this.#processErrorBlocks(blocks)
    }
  }

  #processErrorBlocks(blocks: AnyBlock[]) {
    this.#errors = new Set()
    for (const block of blocks.filter(b => isFilterBlock(b) && b.valid === false && this.activeBlockId !== b.id)) {
      if ((block as FilterBlock).validationMessage) this.#errors.add((block as FilterBlock).validationMessage ?? '')
      if (!(block as FilterBlock).key.valid) this.#errors.add((block as FilterBlock).key.validationMessage ?? '')
      for (const value of (block as FilterBlock).value.values) {
        if (value.valid !== false) continue
        if (!value.value) {
          this.#errors.add(Strings.filterValueEmpty((block as FilterBlock).key.value))
        } else if (value.validationMessage) {
          this.#errors.add(value.validationMessage)
        } else {
          this.#errors.add(
            Strings.filterInvalidValue(
              (block as FilterBlock).key.value,
              sanitizeOperators(getFilterValue(value.value)) ?? '',
            ),
          )
        }
      }
    }
  }

  get activeBlock() {
    return this.blocks[this.activeBlockId] ?? null
  }

  get filterCount() {
    if (!this.isValidated || !checkFilterQuerySync(this)) return this.cachedFilterCount
    const updatedCount = this.blocks.filter(b => isFilterBlock(b) && b.value.values.some(v => v.valid)).length
    this.cachedFilterCount = updatedCount
    return updatedCount
  }
}
